<template>
  <v-menu
    bottom
    offset-y
    :nudge-bottom="2"
    :close-on-content-click="false"
      close-on-scroll
    :min-width="menuWidth"
    :max-width="menuWidth"
    class="rad-menu"
    content-class="rad__menu-content"
    v-model="showMenu"
  >
    <template v-slot:activator="{ on, attrs }">
      <div
          class="rad__trigger-btn rad-card"
          ref="triggerRef"
          v-bind="attrs"
          v-on="on"
      >
        <div class="d-flex rad__gap rad__trigger">
          <Importance :importance="responsibilityImportance" />
          <CommitmentIcon :type="commitmentIconType" class="rad__commit-icon" />
          <ExNewBadgeVue v-if="showNewBadge"/>
          <Icon name="org_icon_responsibility"/>
          <div class="rad__resp-text">
            <span v-if="show_codes">{{ responsibilityCode }}</span>
            <span class="rad__resp-name">
              {{ responsibilityName }}
            </span>
          </div>
          <v-icon color="#344054"
            class="rad__chevron ml-2"
            :class="{ 'rad__chevron--rotated': showMenu }"
          >
            $ex-chevron-down-icon
          </v-icon>
        </div>
      </div>
    </template>
    <v-card class="rad__menu-card pa-0 rad-card">
      <div class="rad__menu">
        <ResponsibilityAssignmentPathRows
          variant="dropdown"
          :roleCode="roleCode"
          :roleName="roleName"
          :roleImportance="roleImportance"
          :positionCode="positionCode"
          :positionName="positionName"
          :positionImportance="positionImportance"
          :positionCommitment="positionCommitment"
          :show_codes="show_codes"
        />
      </div>
    </v-card>
  </v-menu>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import Icon from "@components/icons/Icon.vue";
import Importance from '@components/importance/Importance.vue';
import ExNewBadgeVue from "@components/ExNewBadge/ExNewBadge.vue";
import CommitmentIcon from "@components/CommitmentIcon/CommitmentIcon.vue";
import ResponsibilityAssignmentPathRows from "@components/ResponsibilityAssignmentPathRows/ResponsibilityAssignmentPathRows.vue";
const showMenu = ref(false);
const triggerRef = ref(null);
const measuredTriggerWidth = ref(null);
let resizeObserver = null;

const props = defineProps({
  responsibilityCode: {
    type: String,
    default: "#9999",
  },
  responsibilityName: {
    type: String,
    default: "Responsibility Name",
  },
  roleCode: {
    type: String,
    default: "",
  },
  roleName: {
    type: String,
    default: "",
  },
  positionCode: {
    type: String,
    default: "",
  },
  positionName: {
    type: String,
    default: "",
  },
  responsibilityImportance: {
    type: Number,
    default: 0,
  },
  roleImportance: {
    type: Number,
    default: 0,
  },
  positionImportance: {
    type: Number,
    default: 0,
  },
  positionCommitment: {
    type: String,
    default: "Primary",
  },
  assignedOn: {
    type: Date,
    default: () => new Date("2025-09-01")
  },
  newDuration: {
    type: Number,
    default: 15,
  },
  show_codes: {
    type: Boolean,
    default: false,
  },
  resMenuWidth: {
    type: [String, Number],
    // Use trigger width by default; pass a number (px) or string (e.g. "408" / "408px") to override.
    default: "auto",
  },
});

const parseWidthPx = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed || trimmed === "auto") return null;
  const numeric = Number.parseFloat(trimmed.replace("px", ""));
  return Number.isFinite(numeric) ? numeric : null;
};

const updateMeasuredWidth = () => {
  const el = triggerRef.value;
  if (!el || typeof el.getBoundingClientRect !== "function") return;
  const width = el.getBoundingClientRect().width;
  if (Number.isFinite(width) && width > 0) {
    measuredTriggerWidth.value = Math.round(width);
  }
};

const menuWidth = computed(() => {
  const override = parseWidthPx(props.resMenuWidth);
  if (override) return override;
  return measuredTriggerWidth.value || 320;
});

const showNewBadge = computed(() => {
  const today = new Date();
  const todayPlusDuration = new Date();
  todayPlusDuration.setDate(today.getDate() + props.newDuration);
  const assignedDate = new Date(props.assignedOn);
  return assignedDate < todayPlusDuration;
});

// Normalize positionCommitment to CommitmentIcon type (same variant as position assignment).
const commitmentIconType = computed(() => {
  const c = props.positionCommitment;
  if (!c) return "Primary";
  const s = String(c).toLowerCase();
  if (s.includes("acting")) return "Acting As";
  if (s.includes("back") || s.includes("backup")) return "Backup";
  if (s.includes("preparing")) return "Preparing For";
  return "Primary";
});

// Vuetify 2 doesn't support `scroll-strategy`. To ensure the menu closes while scrolling
// (especially inside scrolling containers like data tables), we also close on any scroll event.
const handleAnyScroll = () => {
  if (showMenu.value) showMenu.value = false;
};

onMounted(() => {
  updateMeasuredWidth();
  // Keep menu width in sync with the trigger width (table resize, responsive layout, etc.)
  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => updateMeasuredWidth());
    if (triggerRef.value) resizeObserver.observe(triggerRef.value);
  }
  window.addEventListener("scroll", handleAnyScroll, true);
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    try {
      resizeObserver.disconnect();
    } catch (e) {
      // ignore
    }
    resizeObserver = null;
  }
  window.removeEventListener("scroll", handleAnyScroll, true);
});

watch(
  () => showMenu.value,
  async (isOpen) => {
    if (!isOpen) return;
    // Ensure we measure after the activator is laid out (important inside tables/virtual lists).
    await nextTick();
    updateMeasuredWidth();
  }
);
</script>

<style>
.rad__menu-content {
  box-shadow: 0px 4px 6px -2px #10182808, 0px 12px 16px -4px #10182814 !important;
  border: 1px solid #EAECF0 !important;
  border-radius: 12px !important;
  overflow: hidden;
}

.rad__trigger {
  align-items: center;
  width: 100%;
  min-width: 0;
}

.rad-card .importance-container {
  height: 22px !important;
}
.rad-card .badge-box {
  height: 24px;
  margin-top: -1px;
  padding: 3px 6px !important;
}
.rad-card .rad__resp-text {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: #475467;
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rad-card .rad__resp-text .rad__resp-name {
  font-weight: 500;
  color: #101828;
}
.rad-card .rad__gap {
  gap: 5px;
}

.rad-card .rad__chevron {
  margin-left: auto !important;
}
.rad-card .rad__responsibility-sec {
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 8px;
}
.rad-card .rad__responsibility-sec .rad__resp-code {
  font-family: Inter;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: #475467;
}
.rad-card .rad__responsibility-sec .rad__resp-name {
  font-family: Inter;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: #101828;
}
.rad-card .rad__commit-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.rad-card .rad__commit-icon svg {
  width: 18px;
  height: 18px;
}

.rad__trigger-btn {
  background: white !important;
  border: 1px solid #D0D5DD;
  border-radius: 8px !important;
  padding: 8px 12px !important;
  width: 320px;
  height: 44px;
  display: flex;
  align-items: center;
  box-shadow: 0px 1px 2px 0px #1018280D;
}
.rad__trigger-btn::before {
  background-color: white !important;
}
.rad__button-text {
  font-size: 14px;
  font-weight: 600 !important;
  line-height: 20px;
  text-decoration-skip-ink: none;
  color: #344054;
  text-transform: capitalize;
  letter-spacing: 0 !important;
}

.rad__button-text.__disabled {
  color: #E0E0E0;
}

::v-deep.v-btn--disabled .rad__chevron svg path {
  stroke: #E0E0E0;
}

.rad__menu-card {
  box-shadow: none !important;
  border-radius: 12px !important;
}

.rad__menu {
  padding: 4px;
}

.rad__item .rad__action-text {
  font-size: 16px;
  font-weight: 500 !important;
  line-height: 24px;
  text-align: left;
  text-underline-position: from-font;
  color: #344054;
}

.rad__chevron {
  transition: transform 0.2s ease;
}

.rad__chevron--rotated {
  transform: rotate(180deg);
}

.v-list-item__icon {
  margin: 8px 0 !important;
}

.v-list-item__content {
  padding: 8px 0 !important;
}
</style>